<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Thumbnail from '../Thumbnail.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import EscalationPopoverCard from './EscalationPopoverCard.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);
const isLoading = ref(false);
const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId },
    name,
  } = route;
  return conversationListPageURL({
    accountId,
    inboxId,
    label,
    teamId,
    conversationType: name === 'conversation_mentions' ? 'mention' : '',
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
const hasEscalation = computed(() => {
  return props.chat?.escalation_status && props.chat?.escalation_status !== 'no_escalation' &&  props.chat?.escalation_status !== 'completed';
});
const escalationButtonLabel = computed(() => {
  if (props.chat?.escalation_status === 'running') return t('Pause');
  if (props.chat?.escalation_status === 'paused') return t('Resume');
  return '';
});

const pauseOrResumeEscalation = async () => {
  try {
    const status = props.chat?.escalation_status;
    console.log("status" , status)
    let action = '';

    if (status === 'running') action = 'pause';
    else if (status === 'paused') action = 'resume';
    else return;

    console.log(`Attempting to ${action} escalation for conversation:`, currentChat.value?.id);
    isLoading.value = true;
    const data = await store.dispatch('PauseResumeEscalation', {
      conversationId: currentChat.value?.id,
      action,
    });

    console.log("Escalation API Response:", data);
  } catch (error) {
    console.error('Error pausing/resuming escalation:', error);
  } finally {
    isLoading.value = false; // Reset loading state
  }
};

const escalationChain = computed(() => {
  const escalations = props.chat?.escalations || [];

  if (escalations.length === 0) return '';

  // Sort by scheduled_at ascending (just in case)
  const sorted = [...escalations].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

  const completed = sorted.filter(e => e.status === 'completed');
  const active = sorted.find(e => e.status !== 'completed');

  const lastCompleted = completed.at(-1); 
  const result = [];

  if (lastCompleted) result.push(lastCompleted.team_name);
  if (active) result.push(active.team_name);

  return result.join(' >> ');
});
// watch(
//   () => hasEscalation.value,
//   (newVal) => {
//     if (newVal) {
//       pauseOrResumeEscalation();
//     }
//   },
//   { immediate: true } // Optional: calls on initial load too
// );
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2 border-b bg-n-background border-n-weak h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Thumbnail
        :src="currentContact.thumbnail"
        :username="currentContact.name"
        :status="currentContact.availability_status"
        size="32px"
        class="flex-shrink-0"
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>


        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <div class="relative group">
        <div
          v-if="escalationChain"
          class="flex items-center min-w-fit h-[26px] rounded-lg bg-n-alpha-1 text-xs font-medium px-2 text-n-slate-11"
        >
          {{ escalationChain }}
        </div>

    <div class="absolute top-7 left-0 hidden group-hover:flex">
      <EscalationPopoverCard :escalations="props.chat.escalations" />
    </div>
    </div>
        <Button
        v-if="hasEscalation"
        :label="isLoading ? '' : escalationButtonLabel"
        size="sm"
        color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-1 flex items-center justify-center"
        @click="pauseOrResumeEscalation"
      >
        <span v-if="isLoading" class="spinner"></span> <!-- Show spinner -->
        <span v-else>{{ escalationButtonLabel }}</span> <!-- Show label when not loading -->
      </Button>
          <MoreActions :conversation-id="currentChat.id" />
      </div>
  </div>
</template>
